
import React, { useId } from "react";
import { Users, ArrowLeft, Sparkles } from "lucide-react";
import AgeMessage from "@/components/age/age-message";
import { URLS } from "@/lib/urls";
import { useNavigate } from "react-router-dom";
import { APP_CLIENT } from "@/constants/config";

function QuranLoveGradeList({ grade }) {
    const navigate = useNavigate();
    const patternId = useId().replace(/:/g, "");
    const isActive = true;

    if (APP_CLIENT === "quranlove" && grade?.id === 14) {
        return null;
    }

    const svg = (
        <svg
            viewBox="0 0 240 240"
            fill="none"
            className="h-full w-full"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id={`${patternId}-gold`}
                    x1="35"
                    y1="35"
                    x2="205"
                    y2="205"
                >
                    <stop offset="0%" stopColor="#F4DFA2" />
                    <stop offset="50%" stopColor="#C8A65C" />
                    <stop offset="100%" stopColor="#F4DFA2" />
                </linearGradient>

                <linearGradient
                    id={`${patternId}-teal`}
                    x1="55"
                    y1="45"
                    x2="185"
                    y2="195"
                >
                    <stop offset="0%" stopColor="#8EE3CF" />
                    <stop offset="100%" stopColor="#2B9F99" />
                </linearGradient>
            </defs>

            <circle
                cx="120"
                cy="120"
                r="100"
                stroke={`url(#${patternId}-gold)`}
                strokeWidth="1"
                strokeOpacity=".6"
            />

            {Array.from({ length: 8 }, (_, i) => (
                <g key={i} transform={`rotate(${i * 45} 120 120)`}>
                    <path
                        d="M120 20 C105 40 90 55 100 75 C108 90 115 95 120 100 C125 95 132 90 140 75 C150 55 135 40 120 20Z"
                        stroke={`url(#${patternId}-gold)`}
                        strokeWidth="1.5"
                    />
                    <path
                        d="M120 35 C110 50 108 65 120 82 C132 65 130 50 120 35Z"
                        stroke={`url(#${patternId}-teal)`}
                        strokeWidth="1.2"
                    />
                    <path
                        d="M120 48 Q102 42 106 30 Q120 36 120 48 Q138 42 134 30"
                        stroke={`url(#${patternId}-teal)`}
                        strokeWidth="1.2"
                    />
                    <path
                        d="M120 45 Q108 52 120 65 Q132 52 120 45Z"
                        fill="#2B9F99"
                        fillOpacity=".25"
                        stroke={`url(#${patternId}-gold)`}
                        strokeWidth="1"
                    />
                </g>
            ))}

            <circle cx="120" cy="120" r="25"
                    stroke={`url(#${patternId}-gold)`}
                    strokeWidth="1.5"
            />
            <circle cx="120" cy="120" r="17"
                    stroke={`url(#${patternId}-teal)`}
                    strokeWidth="1.2"
            />

            {Array.from({ length: 8 }, (_, i) => (
                <path
                    key={`flower-${i}`}
                    d="M120 101 Q128 112 120 120 Q112 112 120 101Z"
                    fill={`url(#${patternId}-gold)`}
                    transform={`rotate(${i * 45} 120 120)`}
                />
            ))}

            <circle cx="120" cy="120" r="4" fill="#F4DFA2" />
        </svg>
    );
    const colors = isActive
        ? {
            text: "text-[#174d49] dark:text-[#d8eee5]",
            muted: "text-[#52736d] dark:text-[#a8c4ba]",
            accent: "text-[#b38b43] dark:text-[#e1c477]",

            button:
                "bg-gradient-to-l from-[#176b60] via-[#21877a] to-[#329c91] hover:from-[#12594f] hover:via-[#176f64] hover:to-[#26867c] dark:from-[#17665b] dark:via-[#218579] dark:to-[#2d9688] dark:hover:from-[#20796b] dark:hover:via-[#2a9788] dark:hover:to-[#3aab9b]",

            border: "border-[#d8c89e]/70 dark:border-[#6d694b]/60",
        }
        : {
            text: "  dark:text-slate-300",
            muted: "text-slate-500 dark:text-slate-400",
            accent: "text-slate-400 dark:text-slate-500",
            button: "bg-slate-400 dark:bg-slate-600",
            border: "border-slate-200 dark:border-slate-700",
        };
    return (
        <article
            dir="rtl"
            className={`
    group relative isolate flex h-full md:min-h-[400px]
    flex-col overflow-hidden rounded-[24px]
    border ${colors.border}
    bg-gradient-to-br from-[#fffefa] via-[#f8fcf8] to-[#edf7f3]
    dark:bg-gradient-to-br dark:from-[#111b19] dark:via-[#172520] dark:to-[#1d3029]
    font-[inherit]
    shadow-[0_5px_20px_rgba(28,83,72,0.07)]
    dark:shadow-[0_5px_24px_rgba(0,0,0,0.25)]
    transition-all duration-300 ease-out
    hover:-translate-y-1.5
    hover:shadow-[0_18px_38px_rgba(28,83,72,0.14)]
    dark:hover:shadow-[0_18px_38px_rgba(0,0,0,0.4)]
    ${isActive ? "" : "grayscale-[0.3] opacity-75"}
`}
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[24px]"
            >
                {/* Soft ambient lighting */}

                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#8EE3CF]/20 dark:bg-[#45b7a1]/10 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-[#F4DFA2]/20 dark:bg-[#c8a65c]/10 blur-3xl" />
                {/* Top-left ornament */}
                <div
                    className="
            absolute -left-14 -top-14
            h-56 w-56
            -rotate-[18deg]
              opacity-[0.50] dark:opacity-[0.12]
            transition-all duration-700 ease-out
            group-hover:rotate-0
            group-hover:scale-110
            group-hover:opacity-[0.28]
        "
                >
                    {svg}
                </div>

                {/* Bottom-right ornament */}
                <div
                    className="
            absolute -bottom-16 -right-14
            h-64 w-64
            rotate-[22deg]
                  opacity-[0.40] dark:opacity-[0.10]

            transition-all duration-700 ease-out
            group-hover:rotate-12
            group-hover:scale-110
            group-hover:opacity-[0.25]
        "
                >
                    {svg}
                </div>

                {/* Subtle golden light accents */}
                <div className="absolute left-[28%] top-10 h-20 w-20 rounded-full bg-[#C8A65C]/[0.06] blur-2xl" />
                <div className="absolute bottom-24 right-[30%] h-24 w-24 rounded-full bg-[#2B9F99]/[0.06] blur-2xl" />

                <div className="absolute left-5 top-5 h-8 w-8 rounded-tl-xl border-l border-t border-[#C8A65C]/30 dark:border-[#d0b66e]/25" />
                <div className="absolute bottom-5 right-5 h-8 w-8 rounded-br-xl border-b border-r border-[#C8A65C]/30 dark:border-[#d0b66e]/25" />

            </div>

            {/* Elegant top border */}
            <div className="relative h-1.5 shrink-0 bg-gradient-to-l from-[#b89550] via-[#57a99a] to-[#176b60]">
                <div className="absolute inset-x-0 top-0 h-px bg-white/80 dark:bg-white/20" />
            </div>

            {/* Card header */}
            <div className="relative flex flex-col items-center px-6 pb-3 pt-7">


                <h3
                    className={`
                        md:min-h-[56px] w-full text-center text-xl
                        font-bold leading-7 ${colors.text}
                    `}
                >
                    {grade?.title}
                </h3>

                {/* Gold ornament divider */}
                <div className="mt-3 flex w-full items-center justify-center gap-3">
                    <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#b89550]/70" />
                    <svg
                        viewBox="0 0 28 14"
                        className="h-3.5 w-7 text-[#b89550]"
                        fill="none"
                    >
                        <path
                            d="M14 1 L19 7 L14 13 L9 7 Z M1 7 H9 M19 7 H27"
                            stroke="currentColor"
                            strokeWidth="1"
                        />
                        <circle cx="14" cy="7" r="1.5" fill="currentColor" />
                    </svg>
                    <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#b89550]/70" />
                </div>
            </div>

            {/* Description and age */}
            <div className="relative flex flex-1 flex-col px-6 pb-5 pt-2">
                <p
                    className={`
                        line-clamp-3 md:min-h-[40px] text-center
                        text-sm leading-7 ${colors.muted}
                    `}
                >
                    {grade?.description}
                </p>

                <div className="mt-auto pt-1">
                    {(grade?.minage || grade?.maxage) && (
                        <div
                            className="
                            flex md:min-h-[58px] items-center justify-center
                            gap-3 rounded-xl
                            border border-[#c6ded5]/80 dark:border-[#35564b]
                            bg-white/65 dark:bg-[#1c2b25]/90
                            px-3 py-3
                            shadow-sm shadow-emerald-900/[0.03]
                            dark:shadow-black/10
                        "
                        >
                            <div className="
    flex h-9 w-9 shrink-0 items-center justify-center
    rounded-lg
    bg-[#e3f2ec] dark:bg-[#294239]
    text-[#26796c] dark:text-[#8ed8c5]
">
                                <Users className="h-[18px] w-[18px]" />
                            </div>

                            <div className="min-w-0 flex-1 text-center">
                                <span className="mb-1 block text-[10px] font-medium text-[#829c92] dark:text-[#91aa9e]">
                                    گروه سنی
                                </span>
                                <span className="text-xs font-semibold leading-6 text-[#37665b] dark:text-[#c1ded2]">
                                    <AgeMessage
                                        minage={grade?.minage}
                                        maxage={grade?.maxage}
                                        minagedate={grade?.minagedate}
                                        maxagedate={grade?.maxagedate}
                                    />
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Consistent CTA */}
            <div className="relative mt-auto px-6 pb-6">
                <button
                    type="button"
                    onClick={() => {
                        navigate(
                            URLS.student.lessons.take +
                            "?grade_id=" +
                            grade.id
                        );
                    }}
                    className={`
                        group/button flex md:min-h-[50px] w-full
                        items-center justify-center gap-2.5
                        rounded-xl px-5 py-3
                        text-sm font-bold text-white
                        shadow-md shadow-emerald-900/10
                        transition-all duration-300
                        hover:-translate-y-0.5 hover:shadow-lg
                        active:translate-y-0 active:scale-[0.98]
                        focus-visible:outline-none
                        focus-visible:ring-2
                        focus-visible:ring-[#329c91]
                        focus-visible:ring-offset-2
                        ${colors.button}
                    `}
                >
                    <span>ثبت‌نام در دوره</span>
                    <ArrowLeft
                        className="h-4 w-4 transition-transform duration-300 group-hover/button:-translate-x-1"
                        strokeWidth={2}
                    />
                </button>

                {/* Bottom ornamental accent */}
                <div className="mt-4 flex items-center justify-center gap-2">
                    <span className="h-px flex-1 bg-gradient-to-l from-[#b89550]/50 to-transparent" />
                    <span className="h-1.5 w-1.5 rotate-45 border border-[#b89550]/80" />
                    <span className="h-px w-6 bg-[#b89550]/40" />
                    <span className="h-1.5 w-1.5 rotate-45 border border-[#b89550]/80" />
                    <span className="h-px flex-1 bg-gradient-to-r from-[#b89550]/50 to-transparent" />
                </div>
            </div>

            {/* Bottom edge */}
            <div className="h-1 shrink-0 bg-gradient-to-l from-[#176b60] via-[#329c91] to-[#b89550]" />
        </article>
    );
}

export default QuranLoveGradeList;
